import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { can, useAuth, ROLE } from '../lib/auth';

/**
 * The printing desk: the Planner's plan for a day, machine by machine, in the
 * order it was planned, and what was printed against each row — the sheets,
 * the wastage, and whether the pass is done or its rest goes to tomorrow.
 * The same plan rows and the same actuals step the Planner uses
 * (/api/planner/plans, PUT /plans/:id, PUT /plans/:id/resolve), so the two
 * screens never disagree.  Changing the plan stays on the Planner.
 */

interface Machine { id: string; name: string; displayName: string }

interface PlanRow {
  planId: number; planDate: string; machineId: string;
  jobId: string; passNo: number; sequence: number;
  plannedQty: number; actualQty: number | null; wastageQty: number | null;
  status: string;
  client: string; productName: string; jobName: string;
  designNo: string; ups: string; topFinish: string;
  colorsFront1: string; colorsFront2: string; colorsBack: string;
  promiseDate: string | null; machineName: string;
  boardStatus: string; coating: string; custSize: string; paperType: string; gsm: string;
  totalPasses: number; isBack?: boolean; isReissued: boolean;
  totalSheets?: string; clientQty?: string;
}

/** The job card's actual sheets and wastage, when the row plans the card's full sheets. */
function cardSplit(r: PlanRow): { base: number; wastage: number } | null {
  const q = Number(r.clientQty) || 0, u = Number(r.ups) || 0, total = Number(r.totalSheets) || 0;
  if (q <= 0 || u <= 0 || total <= 0) return null;
  const base = Math.ceil(q / u);
  if (base > total || total !== (Number(r.plannedQty) || 0)) return null;
  return { base, wastage: total - base };
}

interface PendingActual { date: string; count: number }

/** A job run as the paper Daily Production Report writes it (/api/printing/day). */
interface RunLog {
  logId: number; date: string; machine: string; shift: string; operators: string; planId: number | null;
  jobId: string; mrStart: string; mrEnd: string; prodStart: string; prodEnd: string;
  actualQty: number; wastageQty: number; colourSeq: string; remarks: string; mrMin: number; prodMin: number;
}
interface Stoppage {
  downtimeId: number; date: string; machine: string; shift: string; logId: number | null; jobId: string;
  reason: string; description: string; from: string; to: string; minutes: number;
}
interface DayLog { ready: boolean; logs: RunLog[]; downtime: Stoppage[] }

/** Who is on the machine: the running shifts' operators and their shift. */
interface Crew { shift: string; operators: string }

/** An operator's shift on a machine (/api/printing/shifts). */
interface OpShift {
  shiftId: number; date: string; machine: string; operator: string; shift: string;
  startTime: string; endTime: string; breakMin: number; minutes: number | null;
}
/** Super Admin's lists (/api/printing/lists). */
interface Lists { ready: boolean; operators: string[]; shifts: string[]; reasons: string[] }

/** The time now as HH:MM. */
const nowHHMM = () => { const d = new Date(); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
/** Minutes from start to end, through midnight when the end is earlier. */
function spanMin(a: string, b: string): number {
  if (!/^\d{2}:\d{2}$/.test(a) || !/^\d{2}:\d{2}$/.test(b)) return 0;
  const m = (x: string) => Number(x.slice(0, 2)) * 60 + Number(x.slice(3, 5));
  return ((m(b) - m(a)) % 1440 + 1440) % 1440;
}
/** 200 minutes as 3h 20m. */
const durText = (min: number) => (min <= 0 ? '0m' : `${min >= 60 ? `${Math.floor(min / 60)}h ` : ''}${min % 60 ? `${min % 60}m` : ''}`.trim());
/** 200 minutes as 3:20 (hours). */
const hoursText = (min: number) => `${Math.floor(min / 60)}:${pad2(Math.round(min % 60))}`;

/** The job's board at cutting (GET /api/cutting/board-status). */
interface BoardCut { job: string; issued: number; cut: number; boards: number }

/** Board not issued / not cut / part cut / cut, as one badge. */
function CutBadge({ s }: { s: BoardCut | undefined }) {
  if (!s) return <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Board: checking…</span>;
  const pct = s.issued > 0 ? Math.round((s.cut / s.issued) * 100) : 0;
  const [cls, text] = s.issued <= 0
    ? ['bg-slate-200 text-slate-700', 'Board not issued']
    : s.cut >= s.issued
      ? ['bg-emerald-600 text-white', '✓ Board cut']
      : s.cut > 0
        ? ['bg-amber-100 text-amber-900', `Board part cut · ${fmt(s.cut)} of ${fmt(s.issued)} (${pct}%)`]
        : ['bg-red-100 text-red-800', `Board not cut · ${fmt(s.issued)} issued`];
  return (
    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${cls}`}
      title={s.issued > 0 ? `${fmt(s.cut)} of ${fmt(s.issued)} sheets cut${s.boards > 1 ? ` · ${s.boards} boards` : ''}` : 'No board issued to this job yet'}>
      {text}{s.boards > 1 && s.issued > 0 ? ` · ${s.boards} boards` : ''}
    </span>
  );
}

const MACHINE_KEY = 'kpjm.printing.machine';
const savedMachine = () => { try { return localStorage.getItem(MACHINE_KEY) ?? ''; } catch { return ''; } };
const saveMachine = (m: string) => { try { localStorage.setItem(MACHINE_KEY, m); } catch { /* private window */ } };

const pad2 = (n: number) => String(n).padStart(2, '0');
const isoDay = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const addDays = (iso: string, n: number) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return isoDay(d); };
const dayText = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
const shortJob = (id: string) => id.slice(id.lastIndexOf('/') + 1);
const fmt = (n: number) => n.toLocaleString('en-IN');

/** Which side this row prints, and its colours. */
function passOf(r: PlanRow): { label: string; colors: string } {
  if (r.isBack || r.passNo >= 3) return { label: 'Back', colors: r.colorsBack };
  if (r.passNo === 2) return { label: 'Front 2', colors: r.colorsFront2 };
  return { label: r.totalPasses > 1 ? 'Front' : '', colors: r.colorsFront1 };
}

export function PrintingQueuePage() {
  const { user } = useAuth();
  const canRecord = can(user, 'RECORD_PRINTING') || can(user, 'VIEW_PLANNER');
  const today = isoDay(new Date());
  const [date, setDate] = useState(today);
  const [machine, setMachine] = useState(savedMachine());
  const [recording, setRecording] = useState<PlanRow | null>(null);
  const [saved, setSaved] = useState('');
  const [search, setSearch] = useState('');
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const findQ = useQuery({
    enabled: search.trim().length >= 2,
    queryKey: ['printing-find', search.trim()],
    queryFn: () => api<{ rows: { planId: number; date: string; machine: string; jobId: string; passNo: number; status: string;
      plannedQty: number; actualQty: number | null; client: string; product: string }[] }>(`/api/printing/find?q=${encodeURIComponent(search.trim())}`),
    staleTime: 30_000,
  });
  const [view, setView] = useState<'plan' | 'report'>('plan');
  const [addingDown, setAddingDown] = useState(false);
  const [shiftStep, setShiftStep] = useState<{ step: 'start' | 'end' | 'lists'; shiftId?: number } | null>(null);
  const qc = useQueryClient();
  const superAdmin = user?.rollId === ROLE.SuperAdmin;
  const listsQ = useQuery({ queryKey: ['printing-lists'], queryFn: () => api<Lists>('/api/printing/lists'), staleTime: 5 * 60_000 });
  const shiftsQ = useQuery({
    queryKey: ['printing-shifts', date],
    queryFn: () => api<{ ready: boolean; rows: OpShift[] }>(`/api/printing/shifts?date=${date}`),
  });
  const running = (shiftsQ.data?.rows ?? []).filter((s) => !s.endTime);

  const machines = useQuery({
    queryKey: ['planner-machines'],
    queryFn: () => api<Machine[]>('/api/planner/machines'),
    staleTime: 5 * 60_000,
  });
  // The first machine until one is picked (or the remembered one is gone).
  useEffect(() => {
    const list = machines.data ?? [];
    if (list.length && !list.some((m) => m.id === machine)) setMachine(list[0]!.id);
  }, [machines.data, machine]);

  const plans = useQuery({
    enabled: !!machine,
    queryKey: ['printing-plans', date, machine],
    queryFn: () => api<PlanRow[]>(`/api/planner/plans?date=${date}&machine=${encodeURIComponent(machine)}`),
    // The Planner may reorder, add or move jobs at any time from another
    // desk: look again every 30 seconds, and whenever this window is opened.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  // When the Planner changes the order (or adds / moves jobs), say so once.
  const orderKey = (plans.data ?? []).map((r) => r.planId).join(',');
  const lastOrder = useRef<{ key: string; machine: string; date: string } | null>(null);
  const [reordered, setReordered] = useState<string | null>(null);
  useEffect(() => {
    if (!plans.data) return;
    const prev = lastOrder.current;
    if (prev && prev.machine === machine && prev.date === date && prev.key !== orderKey) {
      const a = prev.key.split(',').filter(Boolean), b = orderKey.split(',').filter(Boolean);
      const sameJobs = a.length === b.length && a.every((x) => b.includes(x));
      setReordered(sameJobs ? 'The Planner changed the order — the list below is the new sequence.'
        : "The Planner changed this day's plan for this machine — the list below is up to date.");
    }
    if (!prev || prev.machine !== machine || prev.date !== date) setReordered(null);
    lastOrder.current = { key: orderKey, machine, date };
  }, [orderKey, machine, date, plans.data]);
  const pending = useQuery({
    enabled: !!machine,
    queryKey: ['printing-pending', machine],
    queryFn: () => api<PendingActual[]>(`/api/planner/pending-actuals?machine=${encodeURIComponent(machine)}`),
  });

  const rows = plans.data ?? [];
  // The plan's order is the order to print in: the first row not yet recorded is next.
  const nextRow = rows.find((r) => r.status === 'planned');
  // The search narrows the day's list; the totals stay the day's.
  const shown = !words.length ? rows : rows.filter((r) => {
    const hay = `${r.jobId} ${r.client} ${r.productName} ${r.jobName} ${r.designNo}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  // Whether each job's board is cut — the first thing the printer needs.
  const jobsKey = [...new Set(rows.map((r) => shortJob(r.jobId)))].sort().join(',');
  const cutQ = useQuery({
    enabled: !!jobsKey,
    queryKey: ['printing-board-cut', jobsKey],
    queryFn: () => api<{ rows: BoardCut[] }>(`/api/cutting/board-status?jobs=${encodeURIComponent(jobsKey)}`),
    staleTime: 60_000,
  });
  const cutOf = new Map((cutQ.data?.rows ?? []).map((s) => [s.job, s]));
  const open = rows.filter((r) => r.status === 'planned');
  const notCut = open.filter((r) => { const s = cutOf.get(shortJob(r.jobId)); return s && s.cut < s.issued; }).length
    + open.filter((r) => { const s = cutOf.get(shortJob(r.jobId)); return s && s.issued <= 0; }).length;
  const plannedSheets = rows.reduce((s, r) => s + (Number(r.plannedQty) || 0), 0);
  const printed = rows.reduce((s, r) => s + (Number(r.actualQty) || 0), 0);
  const wastage = rows.reduce((s, r) => s + (Number(r.wastageQty) || 0), 0);
  const earlier = (pending.data ?? []).filter((p) => p.date !== date);

  const pick = (m: string) => { setMachine(m); saveMachine(m); setSaved(''); };
  const onMachine = running.filter((s) => s.machine === machine);
  const crew: Crew = { shift: onMachine[0]?.shift ?? '', operators: onMachine.map((s) => s.operator).join(', ') };

  // The day's job runs and stoppages on this machine (the paper report).
  const dayQ = useQuery({
    enabled: !!machine && view === 'plan',
    queryKey: ['printing-day', date, machine],
    queryFn: () => api<DayLog>(`/api/printing/day?date=${date}&machine=${encodeURIComponent(machine)}`),
  });
  const logReady = dayQ.data?.ready ?? false;
  const logs = dayQ.data?.logs ?? [];
  const stops = dayQ.data?.downtime ?? [];
  const logOf = new Map(logs.filter((l) => l.planId != null).map((l) => [l.planId!, l]));
  const dayMr = logs.reduce((s, l) => s + l.mrMin, 0);
  const dayProd = logs.reduce((s, l) => s + l.prodMin, 0);
  const dayDown = stops.reduce((s, d) => s + d.minutes, 0);
  const removeStop = useMutation({
    mutationFn: (id: number) => api(`/api/printing/downtime/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['printing-day'] }),
  });

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-4 md:px-6 pt-4 shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Printing</h1>
            <p className="text-xs text-slate-500">What is planned on each machine, and what was printed</p>
          </div>
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
            {([['plan', 'Plan & record'], ['report', 'Monthly report']] as const).map(([k, label]) => (
              <button key={k} onClick={() => setView(k)}
                className={`rounded-md px-4 py-1.5 text-sm font-semibold ${view === k ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
                {label}
              </button>
            ))}
          </div>
          {/* The day */}
          {view === 'plan' && <div className="flex items-center gap-2">
            <button onClick={() => setDate(addDays(date, -1))} aria-label="Previous day"
              className="rounded-lg border border-slate-300 bg-white w-9 h-9 text-slate-600 hover:bg-slate-50">‹</button>
            <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 focus:border-brand-500 focus:outline-none" />
            <button onClick={() => setDate(addDays(date, 1))} aria-label="Next day"
              className="rounded-lg border border-slate-300 bg-white w-9 h-9 text-slate-600 hover:bg-slate-50">›</button>
            {date !== today && (
              <button onClick={() => setDate(today)} className="rounded-lg bg-brand-50 px-3 py-1.5 text-sm font-semibold text-brand-700 hover:bg-brand-100">Today</button>
            )}
          </div>}
        </div>

        {/* Machines */}
        {view === 'plan' && <div className="mt-3 flex flex-wrap gap-2 pb-3">
          {(machines.data ?? []).map((m) => (
            <button key={m.id} onClick={() => pick(m.id)}
              className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
                machine === m.id ? 'border-brand-600 bg-brand-600 text-white shadow-sm' : 'border-slate-300 bg-white text-slate-700 hover:border-brand-400 hover:bg-brand-50'
              }`}>
              {m.displayName}
            </button>
          ))}
          {machines.isLoading && <span className="text-sm text-slate-500">Loading machines…</span>}
          {machines.isError && <span className="text-sm text-red-600">Could not load the machines.</span>}
        </div>}
        {view === 'plan' && machine && (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 py-2.5 text-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">On shift · {machine}</span>
            {shiftsQ.data && !shiftsQ.data.ready ? (
              <span className="text-xs text-amber-700">Shifts can be kept once the database update for printing shifts is run.</span>
            ) : onMachine.length === 0 ? (
              <span className="text-slate-500">Nobody{canRecord && <> · <button className="font-semibold text-emerald-700 hover:underline" onClick={() => setShiftStep({ step: 'start' })}>start a shift</button></>}</span>
            ) : onMachine.map((s) => (
              <span key={s.shiftId} className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 py-1 pl-2.5 pr-1 text-emerald-900">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden />
                <span className="font-semibold">{s.operator}</span>
                <span className="text-xs text-emerald-700">Shift {s.shift} · since {s.startTime}{s.date !== date ? ` (${dayText(s.date)})` : ''}</span>
                {canRecord && (
                  <button onClick={() => setShiftStep({ step: 'end', shiftId: s.shiftId })}
                    className="rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 hover:text-red-700">End</button>
                )}
              </span>
            ))}
            {running.filter((s) => s.machine !== machine).length > 0 && (
              <span className="text-xs text-slate-400">
                Also on shift: {running.filter((s) => s.machine !== machine).map((s) => `${s.operator} (${s.machine})`).join(', ')}
              </span>
            )}
            <div className="ml-auto flex gap-2">
              {canRecord && (
                <>
                  <button onClick={() => setShiftStep({ step: 'start' })}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700">
                    <svg aria-hidden viewBox="0 0 12 12" className="h-3 w-3 fill-current"><path d="M2.5 1.5v9l8-4.5z" /></svg> Start shift
                  </button>
                  <button onClick={() => setShiftStep({ step: 'end' })}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                    <svg aria-hidden viewBox="0 0 12 12" className="h-3 w-3 fill-current text-red-600"><rect x="2" y="2" width="8" height="8" rx="1" /></svg> End shift
                  </button>
                </>
              )}
              {superAdmin && (
                <button onClick={() => setShiftStep({ step: 'lists' })}
                  className="whitespace-nowrap rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Manage lists</button>
              )}
            </div>
          </div>
        )}
      </div>

      {view === 'report' ? <div className="flex-1 min-h-0 overflow-auto p-4 md:p-6"><MonthlyReport machines={(machines.data ?? []).map((m) => m.id)} /></div> : (

      <div className="flex-1 min-h-0 overflow-auto p-4 md:p-6">
        {earlier.length > 0 && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-semibold">Not recorded yet on {machine}:</span>
            {earlier.map((p) => (
              <button key={p.date} onClick={() => setDate(p.date)} className="font-medium underline underline-offset-2 hover:text-amber-700">
                {dayText(p.date)} ({p.count})
              </button>
            ))}
          </div>
        )}

        {saved && <div className="mb-3 text-sm font-medium text-emerald-700">✓ {saved}</div>}

        {/* Where the day stands */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          {([
            ['Jobs planned', fmt(rows.length), 'text-slate-800'],
            ['Sheets planned', fmt(plannedSheets), 'text-slate-800'],
            ['Printed', fmt(printed), 'text-emerald-700'],
            ['To record', fmt(open.length), open.length ? 'text-amber-700' : 'text-slate-400'],
            ['Board not ready', cutQ.data ? fmt(notCut) : '—', notCut ? 'text-red-700' : 'text-emerald-700'],
          ] as const).map(([label, value, cls]) => (
            <div key={label} className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <div className={`text-2xl font-bold ${cls}`}>{value}</div>
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
            </div>
          ))}
        </div>
        {wastage > 0 && <div className="-mt-2 mb-2 text-xs text-slate-500">Wastage recorded: {fmt(wastage)} sheets</div>}
        {logReady && (
          <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm shadow-sm">
            <span><b className="text-slate-500 font-medium">Make-ready</b> <b className="text-violet-700">{durText(dayMr)}</b></span>
            <span><b className="text-slate-500 font-medium">Production</b> <b className="text-emerald-700">{durText(dayProd)}</b></span>
            <span><b className="text-slate-500 font-medium">Down time</b> <b className="text-red-700">{durText(dayDown)}</b></span>
          </div>
        )}
        {dayQ.data && !logReady && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Make-ready, production and down time can be kept once the database update for the printing report is run.
          </div>
        )}

        {reordered && (
          <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-violet-300 bg-violet-50 px-4 py-2.5 text-sm text-violet-900">
            <span><b>Updated:</b> {reordered}</span>
            <button onClick={() => setReordered(null)} className="text-xs font-semibold text-violet-700 hover:underline">OK</button>
          </div>
        )}
        <div className="mb-2 flex items-center justify-end gap-2 text-[11px] text-slate-400">
          <span>Order follows the Planner{plans.dataUpdatedAt ? ` · checked ${new Date(plans.dataUpdatedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}</span>
          <button onClick={() => plans.refetch()} disabled={plans.isFetching}
            className="rounded border border-slate-300 bg-white px-2 py-0.5 font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            {plans.isFetching ? 'Checking…' : '↻ Refresh'}
          </button>
        </div>
        {nextRow && (
          <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm">
            <span className="rounded bg-brand-600 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-white">Next</span>
            <span className="font-bold text-slate-900">#{rows.indexOf(nextRow) + 1} · Job {shortJob(nextRow.jobId)}</span>
            <span className="text-slate-700">{nextRow.client}{nextRow.productName ? ` · ${nextRow.productName}` : ''}</span>
            <span className="text-slate-500">{fmt(Number(nextRow.plannedQty) || 0)} sheets</span>
            <span className="ml-auto text-xs text-slate-500">Print in the plan's order — {open.length} left today</span>
          </div>
        )}

        {/* Find a job: on this machine today, or anywhere on the plan */}
        <div className="mb-4">
          <div className="relative w-full md:w-[28rem]">
            <svg aria-hidden viewBox="0 0 20 20" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 fill-slate-400">
              <path d="M8.5 3a5.5 5.5 0 014.38 8.83l3.65 3.64-1.06 1.06-3.64-3.65A5.5 5.5 0 118.5 3zm0 1.5a4 4 0 100 8 4 4 0 000-8z" />
            </svg>
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') setSearch(''); }}
              placeholder="Search job no, client, product or design"
              className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-9 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
            {search && (
              <button type="button" onClick={() => setSearch('')} aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full w-7 h-7 text-lg leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700">&times;</button>
            )}
          </div>
          {search.trim().length >= 2 && (() => {
            const elsewhere = (findQ.data?.rows ?? []).filter((f) => !(f.machine === machine && f.date === date));
            if (findQ.isLoading) return <div className="mt-2 text-xs text-slate-500">Looking on the whole plan…</div>;
            if (!elsewhere.length) return null;
            return (
              <div className="mt-2 rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  On the plan elsewhere ({elsewhere.length}{elsewhere.length === 50 ? '+' : ''})
                </div>
                <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
                  {elsewhere.map((f) => (
                    <li key={f.planId}>
                      <button type="button" onClick={() => { if (f.machine) pick(f.machine); setDate(f.date); }}
                        className="flex w-full flex-wrap items-center gap-x-4 gap-y-0.5 px-4 py-2 text-left text-sm hover:bg-brand-50">
                        <span className="font-bold text-slate-900">{shortJob(f.jobId)}</span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-700">{f.machine || '—'}</span>
                        <span className="text-slate-600">{dayText(f.date)}</span>
                        <span className={`text-xs font-semibold ${f.status === 'completed' ? 'text-emerald-700' : f.status === 'partial' ? 'text-amber-700' : 'text-slate-500'}`}>
                          {f.status === 'completed' ? `✓ printed ${fmt(Number(f.actualQty) || 0)}` : f.status === 'partial' ? 'part printed' : `${fmt(Number(f.plannedQty) || 0)} planned`}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-xs text-slate-500">{[f.client, f.product].filter(Boolean).join(' · ')}</span>
                        <span className="text-xs font-semibold text-brand-700">Open →</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
        </div>

        {plans.isLoading ? <div className="text-slate-500">Loading…</div>
          : plans.isError ? <div className="text-red-600">Could not load the plan: {(plans.error as Error).message}</div>
          : words.length > 0 && shown.length === 0 && rows.length > 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-slate-500">
              No job on {machine} for {dayText(date)} matches “{search.trim()}”.
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center text-slate-500">
              Nothing planned on {machine || 'this machine'} for {dayText(date)}.
            </div>
          ) : (
            <div className="grid gap-3">
              {shown.map((r) => { const i = rows.indexOf(r);
                const pass = passOf(r);
                const done = r.status === 'completed';
                const part = r.status === 'partial';
                const isNext = r === nextRow;
                return (
                  <div key={r.planId}
                    className={`rounded-xl border bg-white p-4 shadow-sm flex flex-col md:flex-row md:items-center gap-4 ${
                      done ? 'border-emerald-200 bg-emerald-50/40' : part ? 'border-amber-200 bg-amber-50/40'
                        : isNext ? 'border-brand-500 ring-2 ring-brand-500/30' : 'border-slate-200'
                    }`}>
                    {/* The job's place in the plan: print in this order. */}
                    <div className="shrink-0 flex md:flex-col items-center gap-1 md:w-14">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center text-lg font-bold ${
                        done ? 'bg-emerald-600 text-white' : part ? 'bg-amber-500 text-white' : isNext ? 'bg-brand-600 text-white' : 'bg-brand-100 text-brand-700'
                      }`}>{i + 1}</div>
                      <div className={`text-[10px] font-bold uppercase tracking-wide ${
                        done ? 'text-emerald-700' : part ? 'text-amber-700' : isNext ? 'text-brand-700' : 'text-slate-400'
                      }`}>{done ? '✓ Done' : part ? 'Part' : isNext ? 'Next' : `Seq ${i + 1}`}</div>
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-lg font-bold text-slate-900" title={r.jobId}>{shortJob(r.jobId)}</span>
                        {pass.label && <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[11px] font-semibold text-white">{pass.label}</span>}
                        {!cutQ.isError && <CutBadge s={cutOf.get(shortJob(r.jobId))} />}
                        {r.isReissued && <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[11px] font-semibold text-violet-800">Reissued</span>}
                        {r.boardStatus && r.boardStatus !== 'Received' && (
                          <span className="rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800" title="Board purchase status">Purchase: {r.boardStatus}</span>
                        )}
                        {r.promiseDate && <span className="text-xs text-slate-500">DOD {dayText(r.promiseDate)}</span>}
                      </div>
                      <div className="font-semibold text-slate-800 truncate">{r.client}</div>
                      <div className="text-sm text-slate-600 truncate">{r.productName || r.jobName}</div>
                      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                        {pass.colors && <span><b className="text-slate-500 font-medium">Colours</b> {pass.colors}</span>}
                        {r.designNo && <span><b className="text-slate-500 font-medium">Design</b> {r.designNo}</span>}
                        {r.ups && <span><b className="text-slate-500 font-medium">Ups</b> {r.ups}</span>}
                        {r.custSize && <span><b className="text-slate-500 font-medium">Sheet</b> {r.custSize}</span>}
                        {(r.paperType || r.gsm) && <span><b className="text-slate-500 font-medium">Board</b> {[r.paperType, r.gsm && `${r.gsm} GSM`].filter(Boolean).join(' ')}</span>}
                        {r.topFinish && <span><b className="text-slate-500 font-medium">Finish</b> {r.topFinish}</span>}
                        {r.coating && <span><b className="text-slate-500 font-medium">Coating</b> {r.coating}</span>}
                      </div>
                      {logOf.get(r.planId) && (() => {
                        const l = logOf.get(r.planId)!;
                        return (
                          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                            {l.mrMin > 0 && <span className="rounded bg-violet-50 px-1.5 py-0.5 text-violet-800">M/R {l.mrStart}–{l.mrEnd} · {durText(l.mrMin)}</span>}
                            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-800">Run {l.prodStart}–{l.prodEnd} · {durText(l.prodMin)}</span>
                            {l.colourSeq && <span className="text-slate-600"><b className="text-slate-500 font-medium">Sequence</b> {l.colourSeq}</span>}
                            {(l.shift || l.operators) && <span className="text-slate-500">Shift {l.shift}{l.operators ? ` · ${l.operators}` : ''}</span>}
                          </div>
                        );
                      })()}
                    </div>

                    <div className="shrink-0 flex items-center gap-4 md:flex-col md:items-end md:gap-2">
                      <div className="text-right">
                        <div className="text-xl font-bold text-slate-800">{fmt(Number(r.plannedQty) || 0)}</div>
                        <div className="text-[11px] uppercase tracking-wide text-slate-500">sheets planned</div>
                        {cardSplit(r) && (
                          <div className="text-[11px] text-slate-500" title="As on the job card">
                            JC: {fmt(cardSplit(r)!.base)} + {fmt(cardSplit(r)!.wastage)} wastage
                          </div>
                        )}
                      </div>
                      {done || part ? (
                        <div className="text-right">
                          <div className={`text-sm font-semibold ${done ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {done ? '✓ Printed' : 'Part printed'} {fmt(Number(r.actualQty) || 0)}
                            {Number(r.wastageQty) > 0 && <span className="font-normal text-slate-500"> · waste {fmt(Number(r.wastageQty))}</span>}
                          </div>
                          {part && <div className="text-[11px] text-amber-700">rest moved to the next day</div>}
                          {canRecord && (
                            <button onClick={() => { setSaved(''); setRecording(r); }} className="text-xs font-semibold text-brand-700 hover:underline">Correct</button>
                          )}
                        </div>
                      ) : canRecord ? (
                        <button onClick={() => { setSaved(''); setRecording(r); }}
                          className="rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 whitespace-nowrap">
                          Record printed
                        </button>
                      ) : <span className="text-xs text-slate-500">Not recorded</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        {/* Down time on this machine today */}
        {logReady && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
              <div>
                <div className="font-semibold text-slate-800">Down time</div>
                <div className="text-xs text-slate-500">{stops.length ? `${stops.length} stoppage${stops.length === 1 ? '' : 's'} · ${durText(dayDown)}` : 'None recorded for this day'}</div>
              </div>
              {canRecord && (
                <button onClick={() => setAddingDown(true)}
                  className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2 text-sm font-semibold text-red-700 hover:bg-red-100">+ Down time</button>
              )}
            </div>
            {stops.length > 0 && (
              <ul className="divide-y divide-slate-100">
                {stops.map((d) => (
                  <li key={d.downtimeId} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
                    <span className="font-mono text-slate-700">{d.from}–{d.to}</span>
                    <span className="font-semibold text-red-700">{durText(d.minutes)}</span>
                    <span className="font-medium text-slate-800">{d.reason}</span>
                    {d.jobId && <span className="text-xs text-slate-500">Job {shortJob(d.jobId)}</span>}
                    {d.description && <span className="text-xs text-slate-500">{d.description}</span>}
                    {canRecord && (
                      <button onClick={() => { if (confirm(`Delete this stoppage (${d.reason}, ${d.from}–${d.to})?`)) removeStop.mutate(d.downtimeId); }}
                        className="ml-auto rounded-full w-7 h-7 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Delete">&times;</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      )}

      {recording && (
        <RecordModal
          row={recording}
          cut={cutOf.get(shortJob(recording.jobId))}
          machine={machine}
          crew={crew}
          logReady={logReady}
          log={logOf.get(recording.planId)}
          lastEnd={[...logs].sort((a, b) => a.prodEnd.localeCompare(b.prodEnd)).at(-1)?.prodEnd ?? ''}
          seq={rows.indexOf(recording) + 1}
          before={rows.slice(0, Math.max(rows.indexOf(recording), 0)).filter((r) => r.status === 'planned').map((r) => `#${rows.indexOf(r) + 1} ${shortJob(r.jobId)}`)}
          onClose={() => setRecording(null)}
          onSaved={(text) => { setRecording(null); setSaved(text); qc.invalidateQueries({ queryKey: ['printing-day'] }); }}
        />
      )}
      {shiftStep?.step === 'start' && (
        <StartShiftModal date={date} machine={machine} machines={(machines.data ?? []).map((m) => m.id)} lists={listsQ.data}
          running={running} onClose={() => setShiftStep(null)}
          onSaved={(text) => { setShiftStep(null); setSaved(text); qc.invalidateQueries({ queryKey: ['printing-shifts'] }); }} />
      )}
      {shiftStep?.step === 'end' && (
        <EndShiftModal running={running} initialId={shiftStep.shiftId} onClose={() => setShiftStep(null)}
          onStart={() => setShiftStep({ step: 'start' })}
          onSaved={(text) => { setShiftStep(null); setSaved(text); qc.invalidateQueries({ queryKey: ['printing-shifts'] }); }} />
      )}
      {shiftStep?.step === 'lists' && <ListsModal onClose={() => setShiftStep(null)} />}
      {addingDown && (
        <DowntimeModal
          reasons={listsQ.data?.reasons ?? []}
          date={date}
          machine={machine}
          crew={crew}
          jobs={rows.map((r) => ({ jobId: r.jobId, logId: logOf.get(r.planId)?.logId ?? null, label: `${shortJob(r.jobId)} · ${r.client}` }))}
          onClose={() => setAddingDown(false)}
          onSaved={() => { setAddingDown(false); qc.invalidateQueries({ queryKey: ['printing-day'] }); }}
        />
      )}
    </div>
  );
}

/**
 * What was printed on one plan row.  Sheets printed + wastage reaching the
 * plan closes the pass; short of it, the operator says whether the pass is
 * done or its rest goes to the next day — as the Planner asks.  A row
 * already recorded is only corrected (its sheets), never resolved again.
 */
function RecordModal({ row, cut, machine, crew, logReady, log, lastEnd, seq, before, onClose, onSaved }: {
  row: PlanRow; cut?: BoardCut; machine: string; crew: Crew; logReady: boolean; log?: RunLog; lastEnd: string;
  /** Its place in the day's plan, and the jobs ahead of it not yet recorded:
   *  saving moves it up ahead of them (it ran first). */
  seq: number; before: string[];
  onClose: () => void; onSaved: (text: string) => void;
}) {
  const qc = useQueryClient();
  // The paper report's times: make-ready, then the run.  A new run starts its
  // make-ready where the machine's last run ended.
  const [mrStart, setMrStart] = useState(log ? log.mrStart : lastEnd);
  const [mrEnd, setMrEnd] = useState(log?.mrEnd ?? '');
  const [prodStart, setProdStart] = useState(log?.prodStart ?? '');
  const [prodEnd, setProdEnd] = useState(log?.prodEnd ?? '');
  const [colourSeq, setColourSeq] = useState(log?.colourSeq ?? '');
  const mrMin = mrStart && mrEnd ? spanMin(mrStart, mrEnd) : 0;
  const prodMin = prodStart && prodEnd ? spanMin(prodStart, prodEnd) : 0;
  const correcting = row.status !== 'planned';
  const planned = Number(row.plannedQty) || 0;
  // Not recorded yet: start from the job card's actual sheets and wastage.
  const card = correcting ? null : cardSplit(row);
  const [actual, setActual] = useState<number | null>(correcting ? Number(row.actualQty) || 0 : card ? card.base : planned);
  const [waste, setWaste] = useState<number | null>(correcting ? Number(row.wastageQty) || 0 : card ? card.wastage : 0);
  const [rest, setRest] = useState<'keep_open' | 'close'>('keep_open');
  const total = (actual ?? 0) + (waste ?? 0);
  const short = !correcting && total < planned;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mut = useMutation({
    mutationFn: async () => {
      // Printed ahead of its turn: it takes its real place in the plan first.
      if (!correcting && before.length > 0) {
        await api(`/api/printing/plans/${row.planId}/ran-next`, { method: 'POST', body: '{}' });
      }
      if (logReady) {
        await api('/api/printing/logs', {
          method: 'PUT',
          body: JSON.stringify({
            planId: row.planId, workDate: row.planDate, machine, shift: log?.shift || crew.shift, operators: log?.operators || crew.operators,
            jobId: row.jobId, mrStart: mrStart || null, mrEnd: mrEnd || null, prodStart, prodEnd,
            actualQty: actual ?? 0, wastageQty: waste ?? 0, colourSeq: colourSeq || null,
          }),
        });
      }
      await api(`/api/planner/plans/${row.planId}`, { method: 'PUT', body: JSON.stringify({ actualQty: actual ?? 0, wastageQty: waste ?? 0 }) });
      if (!correcting) {
        await api(`/api/planner/plans/${row.planId}/resolve`, { method: 'PUT', body: JSON.stringify({ action: short ? rest : 'close' }) });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['printing-plans'] });
      qc.invalidateQueries({ queryKey: ['printing-pending'] });
      qc.invalidateQueries({ queryKey: ['printing-board-cut'] });
      const moved = !correcting && before.length > 0 ? ` — moved up to #${seq - before.length} in the plan` : '';
      onSaved((correcting ? `Job ${shortJob(row.jobId)} corrected`
        : short && rest === 'keep_open' ? `Job ${shortJob(row.jobId)}: ${fmt(actual ?? 0)} printed, the rest moved to the next day`
        : `Job ${shortJob(row.jobId)} printed`) + moved);
    },
  });
  const blocked = actual == null || actual < 0 ? 'Enter the sheets printed' : (waste ?? 0) < 0 ? 'Wastage cannot be negative'
    : logReady && (!prodStart || !prodEnd) ? 'Enter the production start and end'
    : logReady && !!mrStart !== !!mrEnd ? 'Enter both make-ready times, or neither'
    : null;
  const timeField = 'w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-base text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';
  const nowBtn = (set: (v: string) => void) => (
    <button type="button" onClick={() => set(nowHHMM())} className="mt-1 text-[11px] font-semibold text-brand-700 hover:underline">Now</button>
  );
  const field = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-lg font-semibold text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';
  const pass = passOf(row);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!blocked && !mut.isPending) mut.mutate();
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-3 sm:p-4 z-50">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[94vh] flex flex-col overflow-hidden">
        <div className="px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-brand-700 to-brand-600 text-white">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wider text-white/70">{correcting ? 'Correct what was printed' : 'Record printed'}{seq > 0 ? ` · #${seq} on the plan` : ''}</div>
              <div className="mt-0.5 text-2xl font-bold leading-tight">Job {shortJob(row.jobId)}{pass.label && <span className="ml-2 text-base font-semibold text-white/80">{pass.label}</span>}</div>
              <div className="mt-1 text-sm font-medium text-white/90 truncate">{row.client}</div>
              <div className="text-xs text-white/75 truncate">{row.productName || row.jobName}</div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close"
              className="shrink-0 rounded-full w-9 h-9 flex items-center justify-center text-2xl leading-none text-white/80 hover:bg-white/15 hover:text-white">&times;</button>
          </div>
          {pass.colors && <div className="mt-3 inline-flex rounded-md bg-white/15 px-2.5 py-1 text-xs font-medium">{pass.colors}</div>}
        </div>

        <div className="overflow-y-auto overflow-x-hidden px-5 sm:px-6 py-5 space-y-5">
          {cut && cut.cut < cut.issued && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <b>Board not fully cut:</b> {fmt(cut.cut)} of {fmt(cut.issued)} sheets cut. Check with cutting before printing more than that.
            </div>
          )}
          {cut && cut.issued <= 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <b>No board issued</b> to this job yet.
            </div>
          )}
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-slate-600">Sheets planned</span>
            <span className="text-2xl font-bold text-slate-800">{fmt(planned)}</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="pr-actual" className="block text-sm font-semibold text-slate-700 mb-2">Good sheets printed</label>
              <input id="pr-actual" type="number" inputMode="numeric" min="0" autoFocus value={actual ?? ''} onFocus={(e) => e.target.select()}
                onChange={(e) => setActual(e.target.value ? Number(e.target.value) : null)} className={field} />
              {!correcting && (
                <button type="button" onClick={() => setActual(Math.max(0, planned - (waste ?? 0)))}
                  className="mt-1.5 text-xs font-semibold text-brand-700 hover:underline">All planned</button>
              )}
            </div>
            <div>
              <label htmlFor="pr-waste" className="block text-sm font-semibold text-slate-700 mb-2">Wastage</label>
              <input id="pr-waste" type="number" inputMode="numeric" min="0" value={waste ?? ''} onFocus={(e) => e.target.select()}
                onChange={(e) => setWaste(e.target.value ? Number(e.target.value) : null)} className={field} />
            </div>
          </div>

          {logReady && (
            <div className="space-y-3">
              <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-violet-900">Make-ready (M/R) <span className="font-normal text-violet-700/70">— leave empty if none</span></span>
                  {mrMin > 0 && <span className="text-sm font-bold text-violet-800">{durText(mrMin)}</span>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs text-slate-500 mb-1">Start</label><input type="time" value={mrStart} onChange={(e) => setMrStart(e.target.value)} className={timeField} />{nowBtn(setMrStart)}</div>
                  <div><label className="block text-xs text-slate-500 mb-1">End</label><input type="time" value={mrEnd} onChange={(e) => { setMrEnd(e.target.value); if (!prodStart) setProdStart(e.target.value); }} className={timeField} />{nowBtn((v) => { setMrEnd(v); if (!prodStart) setProdStart(v); })}</div>
                </div>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-emerald-900">Production run</span>
                  {prodMin > 0 && <span className="text-sm font-bold text-emerald-800">{durText(prodMin)}</span>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs text-slate-500 mb-1">Start</label><input type="time" value={prodStart} onChange={(e) => setProdStart(e.target.value)} className={timeField} />{nowBtn(setProdStart)}</div>
                  <div><label className="block text-xs text-slate-500 mb-1">End</label><input type="time" value={prodEnd} onChange={(e) => setProdEnd(e.target.value)} className={timeField} />{nowBtn(setProdEnd)}</div>
                </div>
              </div>
              <div>
                <label htmlFor="pr-seq" className="block text-sm font-semibold text-slate-700 mb-1">Colour sequence <span className="font-normal text-slate-400">(optional)</span></label>
                <input id="pr-seq" value={colourSeq} onChange={(e) => setColourSeq(e.target.value)} placeholder="e.g. K C M Y 512"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
              </div>
              {!log && (
                <div className="text-xs text-slate-500">
                  {crew.operators ? `Shift ${crew.shift} · ${crew.operators}` : 'Nobody is on shift on this machine — use Start shift above so the operators are kept.'}
                </div>
              )}
            </div>
          )}

          <div className="text-sm text-slate-600">
            {fmt(actual ?? 0)} printed + {fmt(waste ?? 0)} wastage = <b className="text-slate-800">{fmt(total)}</b> of {fmt(planned)}
            {total > planned && <span className="text-amber-700"> · {fmt(total - planned)} more than planned</span>}
          </div>

          {short && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
              <div className="text-sm font-semibold text-amber-900">{fmt(planned - total)} sheets short of the plan. What about the rest?</div>
              {([
                ['keep_open', 'Print the rest tomorrow', 'The rest goes on the next day’s plan for this machine.'],
                ['close', 'This pass is done', 'Close it as it is; nothing more to print.'],
              ] as const).map(([k, title, sub]) => (
                <label key={k} className={`flex cursor-pointer items-start gap-3 rounded-lg border bg-white p-3 ${rest === k ? 'border-brand-600 ring-2 ring-brand-500/20' : 'border-slate-200'}`}>
                  <input type="radio" name="rest" className="mt-1" checked={rest === k} onChange={() => setRest(k)} />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">{title}</span>
                    <span className="block text-xs text-slate-500">{sub}</span>
                  </span>
                </label>
              ))}
            </div>
          )}

          {mut.isError && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">Could not save: {(mut.error as Error).message}</div>}
        </div>

        <div className="px-5 sm:px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500">{blocked ?? ''}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-200/60">Cancel</button>
            <button type="submit" disabled={!!blocked || mut.isPending}
              className="rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {mut.isPending ? 'Saving…' : correcting ? 'Save correction' : short && rest === 'keep_open' ? `Save · ${fmt(actual ?? 0)} printed, rest tomorrow` : `Save · ${fmt(actual ?? 0)} printed`}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}


/** A stoppage on the machine: why, from, to — on a job or just the machine. */
function DowntimeModal({ reasons, date, machine, crew, jobs, onClose, onSaved }: {
  reasons: string[]; date: string; machine: string; crew: Crew; jobs: { jobId: string; logId: number | null; label: string }[];
  onClose: () => void; onSaved: () => void;
}) {
  const [reason, setReason] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [job, setJob] = useState('');
  const [note, setNote] = useState('');
  const minutes = from && to ? spanMin(from, to) : 0;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const picked = jobs.find((j) => j.jobId === job);
  const mut = useMutation({
    mutationFn: () => api('/api/printing/downtime', {
      method: 'POST',
      body: JSON.stringify({ workDate: date, machine, shift: crew.shift, logId: picked?.logId ?? null, jobId: job || null, reason, description: note || null, from, to }),
    }),
    onSuccess: onSaved,
  });
  const blocked = !reason ? 'Pick the reason' : !from || !to ? 'Enter from and to' : minutes === 0 ? 'From and to are the same'
    : reason === 'Other' && !note.trim() ? 'Say what it was' : null;
  const timeField = 'w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-base text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-3 sm:p-4 z-50">
      <form onSubmit={(e) => { e.preventDefault(); if (!blocked && !mut.isPending) mut.mutate(); }} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[94vh] flex flex-col overflow-hidden">
        <div className="px-5 sm:px-6 py-4 bg-gradient-to-br from-red-700 to-red-600 text-white flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-white/70">{machine} · {dayText(date)}</div>
            <div className="mt-0.5 text-xl font-bold leading-tight">Down time</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="shrink-0 rounded-full w-9 h-9 flex items-center justify-center text-2xl leading-none text-white/80 hover:bg-white/15 hover:text-white">&times;</button>
        </div>
        <div className="overflow-y-auto overflow-x-hidden px-5 sm:px-6 py-5 space-y-5">
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2">Why did it stop?</div>
            <div className="flex flex-wrap gap-2">
              {reasons.length === 0 && <span className="text-sm text-slate-500">Loading the reasons…</span>}
              {reasons.map((r) => (
                <button key={r} type="button" onClick={() => setReason(r)}
                  className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                    reason === r ? 'border-red-600 bg-red-600 text-white shadow-sm' : 'border-slate-300 bg-white text-slate-700 hover:border-red-300 hover:bg-red-50'
                  }`}>{reason === r && '✓ '}{r}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">From</label>
              <input type="time" value={from} onChange={(e) => setFrom(e.target.value)} className={timeField} />
              <button type="button" onClick={() => setFrom(nowHHMM())} className="mt-1 text-[11px] font-semibold text-brand-700 hover:underline">Now</button>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">To</label>
              <input type="time" value={to} onChange={(e) => setTo(e.target.value)} className={timeField} />
              <button type="button" onClick={() => setTo(nowHHMM())} className="mt-1 text-[11px] font-semibold text-brand-700 hover:underline">Now</button>
            </div>
          </div>
          {minutes > 0 && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">Stopped for <b>{durText(minutes)}</b>{from && to && to < from ? ' (into the next morning)' : ''}</div>}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">During which job? <span className="font-normal text-slate-400">(optional)</span></label>
            <select value={job} onChange={(e) => setJob(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
              <option value="">Not on a job / the machine</option>
              {jobs.map((j) => <option key={j.jobId} value={j.jobId}>{j.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Note {reason === 'Other' ? '*' : <span className="font-normal text-slate-400">(optional)</span>}</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. coating unit pressure problem"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          </div>
          {mut.isError && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">Could not save: {(mut.error as Error).message}</div>}
        </div>
        <div className="px-5 sm:px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500">{blocked ?? ''}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-200/60">Cancel</button>
            <button type="submit" disabled={!!blocked || mut.isPending}
              className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {mut.isPending ? 'Saving…' : minutes ? `Save · ${durText(minutes)}` : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

interface MachineMonth {
  machine: string; mrMin: number; prodMin: number; downMin: number; runs: number; makeReadies: number;
  printed: number; wastage: number; daysWorked: number;
}
interface ReasonMonth { reason: string; minutes: number; times: number; byMachine: Record<string, number> }
interface DayMonth extends MachineMonth { date: string }
interface OperatorMonth { operator: string; shifts: number; minutes: number; open: number; machines: string[] }
interface MonthReport { ready: boolean; month: string; machines: MachineMonth[]; reasons: ReasonMonth[]; days: DayMonth[]; operators?: OperatorMonth[] }

/** The month on each machine: make-ready, production and down time, and why it stopped. */
function MonthlyReport({ machines }: { machines: string[] }) {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${pad2(now.getMonth() + 1)}`);
  const [machine, setMachine] = useState('');
  const q = useQuery({
    queryKey: ['printing-report', month, machine],
    queryFn: () => api<MonthReport>(`/api/printing/report?month=${month}&machine=${encodeURIComponent(machine)}`),
  });
  const m = q.data?.machines ?? [];
  const sum = (k: keyof MachineMonth) => m.reduce((s, x) => s + (Number(x[k]) || 0), 0);
  const mr = sum('mrMin'), prod = sum('prodMin'), down = sum('downMin'), printed = sum('printed'), waste = sum('wastage');
  const worked = mr + prod + down;
  const pct = (n: number) => (worked > 0 ? Math.round((n / worked) * 100) : 0);
  const maxReason = Math.max(1, ...(q.data?.reasons ?? []).map((r) => r.minutes));

  function download() {
    const lines: string[][] = [
      ['Printing report', month, machine || 'All machines'], [],
      ['Machine', 'Days', 'Jobs run', 'Make-readies', 'Make-ready (h)', 'Avg make-ready (min)', 'Production (h)', 'Down time (h)', 'Sheets printed', 'Wastage', 'Sheets / production hour'],
      ...m.map((x) => [x.machine, String(x.daysWorked), String(x.runs), String(x.makeReadies), hoursText(x.mrMin),
        x.makeReadies ? String(Math.round(x.mrMin / x.makeReadies)) : '', hoursText(x.prodMin), hoursText(x.downMin),
        String(x.printed), String(x.wastage), x.prodMin ? String(Math.round(x.printed / (x.prodMin / 60))) : '']),
      [], ['Down time reason', 'Times', 'Hours', ...machines],
      ...(q.data?.reasons ?? []).map((r) => [r.reason, String(r.times), hoursText(r.minutes), ...machines.map((mc) => (r.byMachine[mc] ? hoursText(r.byMachine[mc]) : ''))]),
      [], ['Operator', 'Shifts', 'Hours worked', 'Machines'],
      ...(q.data?.operators ?? []).map((o) => [o.operator, String(o.shifts), hoursText(o.minutes), o.machines.join(' / ')]),
      [], ['Date', 'Machine', 'Jobs run', 'Make-ready (h)', 'Production (h)', 'Down time (h)', 'Sheets printed', 'Wastage'],
      ...(q.data?.days ?? []).map((d) => [d.date, d.machine, String(d.runs), hoursText(d.mrMin), hoursText(d.prodMin), hoursText(d.downMin), String(d.printed), String(d.wastage)]),
    ];
    const csv = lines.map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `printing-report-${month}${machine ? `-${machine}` : ''}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
        <select value={machine} onChange={(e) => setMachine(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
          <option value="">All machines</option>
          {machines.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <button onClick={download} disabled={!m.length}
          className="ml-auto rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50">Download for Excel</button>
      </div>

      {q.isLoading ? <div className="text-slate-500">Loading…</div>
        : q.isError ? <div className="text-red-600">Could not load the report: {(q.error as Error).message}</div>
        : q.data && !q.data.ready ? <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">The database update for the printing report has not been run yet.</div>
        : !m.length ? <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center text-slate-500">Nothing recorded for this month yet.</div>
        : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {([
                ['Make-ready', durText(mr), `${pct(mr)}% of the time`, 'text-violet-700'],
                ['Production', durText(prod), `${pct(prod)}% of the time`, 'text-emerald-700'],
                ['Down time', durText(down), `${pct(down)}% of the time`, 'text-red-700'],
                ['Sheets printed', fmt(printed), `wastage ${fmt(waste)}`, 'text-slate-800'],
              ] as const).map(([label, value, sub, cls]) => (
                <div key={label} className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <div className={`text-2xl font-bold ${cls}`}>{value}</div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
                  <div className="text-[11px] text-slate-400">{sub}</div>
                </div>
              ))}
            </div>
            {worked > 0 && (
              <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100" title="Make-ready · production · down time">
                <div className="bg-violet-500" style={{ width: `${pct(mr)}%` }} />
                <div className="bg-emerald-500" style={{ width: `${pct(prod)}%` }} />
                <div className="bg-red-500" style={{ width: `${pct(down)}%` }} />
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left">Machine</th>
                    <th className="px-4 py-3 text-right">Days</th>
                    <th className="px-4 py-3 text-right">Jobs run</th>
                    <th className="px-4 py-3 text-right">Make-ready</th>
                    <th className="px-4 py-3 text-right">Avg M/R</th>
                    <th className="px-4 py-3 text-right">Production</th>
                    <th className="px-4 py-3 text-right">Down time</th>
                    <th className="px-4 py-3 text-right">Printed</th>
                    <th className="px-4 py-3 text-right">Wastage</th>
                    <th className="px-4 py-3 text-right">Sheets / hr</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {m.map((x) => (
                    <tr key={x.machine} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-semibold text-slate-900">{x.machine}</td>
                      <td className="px-4 py-2.5 text-right">{x.daysWorked}</td>
                      <td className="px-4 py-2.5 text-right">{x.runs}</td>
                      <td className="px-4 py-2.5 text-right text-violet-700 font-medium">{durText(x.mrMin)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{x.makeReadies ? durText(Math.round(x.mrMin / x.makeReadies)) : '—'}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-700 font-medium">{durText(x.prodMin)}</td>
                      <td className="px-4 py-2.5 text-right text-red-700 font-medium">{durText(x.downMin)}</td>
                      <td className="px-4 py-2.5 text-right">{fmt(x.printed)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{fmt(x.wastage)}{x.printed ? ` (${((x.wastage / (x.printed + x.wastage)) * 100).toFixed(1)}%)` : ''}</td>
                      <td className="px-4 py-2.5 text-right">{x.prodMin ? fmt(Math.round(x.printed / (x.prodMin / 60))) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(q.data?.operators ?? []).length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
                <div className="border-b border-slate-100 px-4 py-3 font-semibold text-slate-800">Operators</div>
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr><th className="px-4 py-2 text-left">Operator</th><th className="px-4 py-2 text-right">Shifts</th>
                      <th className="px-4 py-2 text-right">Hours worked</th><th className="px-4 py-2 text-left">Machines</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(q.data?.operators ?? []).map((o) => (
                      <tr key={o.operator}>
                        <td className="px-4 py-2 font-medium text-slate-800">{o.operator}</td>
                        <td className="px-4 py-2 text-right">{o.shifts}{o.open ? <span className="text-xs text-amber-700"> ({o.open} not ended)</span> : ''}</td>
                        <td className="px-4 py-2 text-right">{durText(o.minutes)}</td>
                        <td className="px-4 py-2 text-slate-600">{o.machines.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-4 py-3 font-semibold text-slate-800">Down time by reason</div>
              {(q.data?.reasons ?? []).length === 0 ? <div className="px-4 py-6 text-sm text-slate-500">No down time recorded.</div> : (
                <ul className="divide-y divide-slate-100">
                  {(q.data?.reasons ?? []).map((r) => (
                    <li key={r.reason} className="px-4 py-2.5">
                      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                        <span className="font-medium text-slate-800">{r.reason} <span className="text-xs font-normal text-slate-500">· {r.times} time{r.times === 1 ? '' : 's'}</span></span>
                        <span className="font-semibold text-red-700">{durText(r.minutes)}</span>
                      </div>
                      <div className="mt-1 h-2 w-full rounded-full bg-slate-100">
                        <div className="h-2 rounded-full bg-red-500" style={{ width: `${Math.round((r.minutes / maxReason) * 100)}%` }} />
                      </div>
                      {Object.keys(r.byMachine).length > 1 && (
                        <div className="mt-1 text-[11px] text-slate-500">{Object.entries(r.byMachine).map(([mc, min]) => `${mc} ${durText(min)}`).join(' · ')}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
              <div className="border-b border-slate-100 px-4 py-3 font-semibold text-slate-800">Day by day</div>
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Machine</th>
                    <th className="px-4 py-2 text-right">Jobs</th><th className="px-4 py-2 text-right">Make-ready</th>
                    <th className="px-4 py-2 text-right">Production</th><th className="px-4 py-2 text-right">Down time</th>
                    <th className="px-4 py-2 text-right">Printed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(q.data?.days ?? []).map((d) => (
                    <tr key={`${d.date}|${d.machine}`}>
                      <td className="px-4 py-2 whitespace-nowrap">{dayText(d.date)}</td>
                      <td className="px-4 py-2">{d.machine}</td>
                      <td className="px-4 py-2 text-right">{d.runs}</td>
                      <td className="px-4 py-2 text-right text-violet-700">{durText(d.mrMin)}</td>
                      <td className="px-4 py-2 text-right text-emerald-700">{durText(d.prodMin)}</td>
                      <td className="px-4 py-2 text-right text-red-700">{durText(d.downMin)}</td>
                      <td className="px-4 py-2 text-right">{fmt(d.printed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
}

const chipCls = (on: boolean) => `rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
  on ? 'border-brand-600 bg-brand-600 text-white shadow-sm' : 'border-slate-300 bg-white text-slate-700 hover:border-brand-400 hover:bg-brand-50'}`;
const timeInputCls = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';

/** The frame the shift steps share: coloured header, scrolling body, footer. */
function ShiftDialog({ tone, title, kicker, onClose, onSubmit, busy, footer, children }: {
  tone: 'start' | 'end' | 'lists'; title: string; kicker: string; onClose: () => void; onSubmit?: () => void; busy?: boolean;
  footer: ReactNode; children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);
  const head = tone === 'start' ? 'from-emerald-700 to-emerald-600' : tone === 'end' ? 'from-slate-800 to-slate-700' : 'from-brand-700 to-brand-600';
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-3 sm:p-4 z-50">
      <form onSubmit={(e) => { e.preventDefault(); onSubmit?.(); }} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[94vh] flex flex-col overflow-hidden">
        <div className={`px-5 sm:px-6 py-4 bg-gradient-to-br ${head} text-white flex items-start justify-between gap-3`}>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-white/70">{kicker}</div>
            <div className="mt-0.5 text-xl font-bold leading-tight">{title}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="shrink-0 rounded-full w-9 h-9 flex items-center justify-center text-2xl leading-none text-white/80 hover:bg-white/15 hover:text-white">&times;</button>
        </div>
        <div className="overflow-y-auto overflow-x-hidden px-5 sm:px-6 py-5 space-y-5">{children}</div>
        <div className="px-5 sm:px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">{footer}</div>
      </form>
    </div>
  );
}

/** An operator starts a shift: who, on which machine, which shift, from when. */
function StartShiftModal({ date, machine, machines, lists, running, onClose, onSaved }: {
  date: string; machine: string; machines: string[]; lists?: Lists; running: OpShift[];
  onClose: () => void; onSaved: (text: string) => void;
}) {
  const [operator, setOperator] = useState('');
  const [onMc, setOnMc] = useState(machine);
  const shifts = lists?.shifts ?? [];
  const [shift, setShift] = useState('');
  const [start, setStart] = useState(nowHHMM());
  const [day, setDay] = useState(date);
  const chosenShift = shift || shifts[0] || '';
  const busyOn = new Map(running.filter((s) => s.date === day).map((s) => [s.operator, s]));
  const mut = useMutation({
    mutationFn: () => api('/api/printing/shifts/start', {
      method: 'POST', body: JSON.stringify({ workDate: day, machine: onMc, operatorName: operator, shift: chosenShift, startTime: start }),
    }),
    onSuccess: () => onSaved(`${operator}'s shift started on ${onMc}`),
  });
  const blocked = !operator ? 'Pick who is starting' : !onMc ? 'Pick the machine' : !chosenShift ? 'Pick the shift' : !start ? 'Enter the start time' : null;
  const err = (mut.error as Error | null)?.message;
  return (
    <ShiftDialog tone="start" kicker="Printing" title="Start shift" onClose={onClose} busy={mut.isPending}
      onSubmit={() => { if (!blocked && !mut.isPending) mut.mutate(); }}
      footer={<>
        <span className="text-xs text-slate-500">{blocked ?? (busyOn.has(operator) ? `Corrects ${operator}'s start` : '')}</span>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-200/60">Cancel</button>
          <button type="submit" disabled={!!blocked || mut.isPending}
            className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {mut.isPending ? 'Saving…' : operator ? `Start ${operator}'s shift` : 'Start shift'}
          </button>
        </div>
      </>}>
      <div>
        <div className="text-sm font-semibold text-slate-700 mb-2">Who is starting?</div>
        <div className="flex flex-wrap gap-2">
          {(lists?.operators ?? []).map((n) => (
            <button key={n} type="button" onClick={() => setOperator(n)} className={chipCls(operator === n)}
              title={busyOn.has(n) ? `On shift on ${busyOn.get(n)!.machine} since ${busyOn.get(n)!.startTime}` : undefined}>
              {operator === n && '✓ '}{n}{busyOn.has(n) && <span className="ml-1 text-[10px] font-semibold opacity-80">· on {busyOn.get(n)!.machine}</span>}
            </button>
          ))}
          {lists && lists.operators.length === 0 && <span className="text-sm text-slate-500">No operators yet — Super Admin adds them under Manage lists.</span>}
        </div>
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-700 mb-2">Machine</div>
        <div className="flex flex-wrap gap-2">
          {machines.map((m) => <button key={m} type="button" onClick={() => setOnMc(m)} className={chipCls(onMc === m)}>{onMc === m && '✓ '}{m}</button>)}
        </div>
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-700 mb-2">Shift</div>
        <div className="flex flex-wrap gap-2">
          {shifts.map((s) => <button key={s} type="button" onClick={() => setShift(s)} className={chipCls(chosenShift === s)}>{chosenShift === s && '✓ '}Shift {s}</button>)}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Start time</label>
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} className={timeInputCls} />
          <button type="button" onClick={() => setStart(nowHHMM())} className="mt-1 text-xs font-semibold text-brand-700 hover:underline">Now</button>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Date</label>
          <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className={timeInputCls} />
        </div>
      </div>
      {err && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{shiftError(err)}</div>}
    </ShiftDialog>
  );
}

/** A shift ends: whose, when, and the break taken. */
function EndShiftModal({ running, initialId, onClose, onStart, onSaved }: {
  running: OpShift[]; initialId?: number; onClose: () => void; onStart: () => void; onSaved: (text: string) => void;
}) {
  const [picked, setPicked] = useState<number | null>(initialId ?? null);
  const sel = running.find((s) => s.shiftId === picked) ?? (running.length === 1 ? running[0] : undefined);
  const [end, setEnd] = useState(nowHHMM());
  const [brk, setBrk] = useState<number | null>(30);
  const worked = sel && end ? Math.max(spanMin(sel.startTime, end) - (brk ?? 0), 0) : null;
  const mut = useMutation({
    mutationFn: () => api(`/api/printing/shifts/${sel!.shiftId}/end`, { method: 'POST', body: JSON.stringify({ endTime: end, breakMin: brk ?? 0 }) }),
    onSuccess: () => onSaved(`${sel!.operator}'s shift ended`),
  });
  const blocked = !sel ? 'Pick whose shift is ending' : !end ? 'Enter the end time' : null;
  const err = (mut.error as Error | null)?.message;
  return (
    <ShiftDialog tone="end" kicker="Printing" title="End shift" onClose={onClose} busy={mut.isPending}
      onSubmit={() => { if (!blocked && !mut.isPending) mut.mutate(); }}
      footer={<>
        <span className="text-xs text-slate-500">{blocked ?? ''}</span>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-200/60">Cancel</button>
          <button type="submit" disabled={!!blocked || mut.isPending}
            className="rounded-lg bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed">
            {mut.isPending ? 'Saving…' : sel ? `End ${sel.operator}'s shift${worked != null ? ` · ${durText(worked)}` : ''}` : 'End shift'}
          </button>
        </div>
      </>}>
      <div>
        <div className="text-sm font-semibold text-slate-700 mb-2">Whose shift is ending?</div>
        {running.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
            Nobody is on shift. <button type="button" onClick={onStart} className="font-semibold text-emerald-700 hover:underline">Start a shift</button>
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {running.map((s) => (
              <button key={s.shiftId} type="button" onClick={() => setPicked(s.shiftId)}
                className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${sel?.shiftId === s.shiftId ? 'border-brand-600 bg-brand-50 ring-2 ring-brand-500/30' : 'border-slate-300 bg-white hover:border-brand-400'}`}>
                <div className="font-semibold text-slate-800">{sel?.shiftId === s.shiftId && '✓ '}{s.operator}</div>
                <div className="text-xs text-slate-500">{s.machine} · Shift {s.shift} · since {s.startTime}{s.date ? ` · ${dayText(s.date)}` : ''}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">End time</label>
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className={timeInputCls} />
          <button type="button" onClick={() => setEnd(nowHHMM())} className="mt-1 text-xs font-semibold text-brand-700 hover:underline">Now</button>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Break (minutes)</label>
          <input type="number" min="0" inputMode="numeric" value={brk ?? ''} onFocus={(e) => e.target.select()}
            onChange={(e) => setBrk(e.target.value ? Number(e.target.value) : null)} className={timeInputCls} />
          <div className="mt-1.5 flex gap-1.5">
            {[0, 30, 60].map((m) => (
              <button key={m} type="button" onClick={() => setBrk(m)}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${brk === m ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-brand-400'}`}>{m}</button>
            ))}
          </div>
        </div>
      </div>
      {sel && worked != null && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-slate-600">{sel.startTime} → {end}{(brk ?? 0) > 0 ? ` · less ${brk} min break` : ''}{spanMin(sel.startTime, end) > 0 && end < sel.startTime ? ' · next morning' : ''}</span>
          <span className="text-2xl font-bold text-slate-800">{durText(worked)}</span>
        </div>
      )}
      {err && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{shiftError(err)}</div>}
    </ShiftDialog>
  );
}

/** Super Admin: the operators, the shifts and the down-time reasons. */
function ListsModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['printing-lists'], queryFn: () => api<Lists>('/api/printing/lists') });
  const [adding, setAdding] = useState<Record<string, string>>({});
  const done = () => qc.invalidateQueries({ queryKey: ['printing-lists'] });
  const add = useMutation({
    mutationFn: (v: { kind: string; name: string }) => api('/api/printing/lists', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: (_r, v) => { setAdding((a) => ({ ...a, [v.kind]: '' })); done(); },
  });
  const remove = useMutation({
    mutationFn: (v: { kind: string; name: string }) => api(`/api/printing/lists/${v.kind}/${encodeURIComponent(v.name)}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const sections: [string, string, string[], string][] = [
    ['OPERATOR', 'Operators', q.data?.operators ?? [], 'New operator\'s name'],
    ['SHIFT', 'Shifts', q.data?.shifts ?? [], 'New shift, e.g. C'],
    ['REASON', 'Down-time reasons', q.data?.reasons ?? [], 'New reason'],
  ];
  const err = (add.error ?? remove.error) as Error | null;
  return (
    <ShiftDialog tone="lists" kicker="Printing · Super Admin" title="Manage lists" onClose={onClose}
      footer={<><span className="text-xs text-slate-500">A removed name stays on past entries.</span>
        <button type="button" onClick={onClose} className="rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700">Done</button></>}>
      {q.data && !q.data.ready && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">Run the printing shifts database update first.</div>}
      {sections.map(([kind, title, names, ph]) => (
        <div key={kind} className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</div>
          <div className="flex flex-wrap gap-2">
            {names.map((n) => (
              <span key={n} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white pl-3 pr-1 py-1 text-sm text-slate-700">
                {kind === 'SHIFT' ? `Shift ${n}` : n}
                <button type="button" title={`Remove ${n}`} disabled={remove.isPending}
                  onClick={() => { if (confirm(`Remove "${n}"?\n\nPast entries keep it.`)) remove.mutate({ kind, name: n }); }}
                  className="w-6 h-6 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600">&times;</button>
              </span>
            ))}
            {names.length === 0 && <span className="text-sm text-slate-500">None yet.</span>}
          </div>
          <div className="flex gap-2">
            <input value={adding[kind] ?? ''} onChange={(e) => setAdding((a) => ({ ...a, [kind]: e.target.value }))} placeholder={ph}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const n = (adding[kind] ?? '').trim(); if (n) add.mutate({ kind, name: n }); } }}
              className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
            <button type="button" disabled={!(adding[kind] ?? '').trim() || add.isPending}
              onClick={() => add.mutate({ kind, name: (adding[kind] ?? '').trim() })}
              className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">Add</button>
          </div>
        </div>
      ))}
      {err && <div className="text-xs text-red-600">{shiftError(err.message)}</div>}
    </ShiftDialog>
  );
}

function shiftError(m: string): string {
  switch (m) {
    case 'migration_needed': return 'The database update for printing shifts has not been run yet.';
    case 'super_admin_only': return 'Only Super Admin can change the lists.';
    case 'shift_already_ended': return "That operator's shift for the day has already ended.";
    case 'shift_not_open': return 'That shift has already ended — refresh.';
    default: return `Could not save: ${m}`;
  }
}
